import { Check, SkipNext } from '@mui/icons-material';
import { Box, Button, Stack, Tooltip, Typography } from '@mui/material';
import { X } from 'lucide-react';
import { Fragment } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomstructureset';
import useStructureSet from '~/hooks/structure-set/useStructureSet';
import {
    ROIState,
    selectedROIState,
} from '~/state/atoms/structureSet/roiAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';

export default function ReviewControls({
    roi,
    onStatusChange,
}: {
    roi: ROIState;
    onStatusChange: (newStatus: DICOMApprovalStatus) => void;
}) {
    const currentStructureSet = useRecoilValue(currentStructureSetState);
    const { setStructureSetApprovalStatus } = useStructureSet();
    const setSelectedROI = useSetRecoilState<number>(selectedROIState);

    const handleReview = (status?: DICOMApprovalStatus) => {
        if (!currentStructureSet?.roiArray) return;

        const roiArray = currentStructureSet.roiArray.sort((a, b) =>
            a.name.localeCompare(b.name)
        );
        const currentIndex = roiArray.findIndex(
            (r) => r.roiNumber === roi.roiNumber
        );

        if (currentIndex !== -1) {
            const nextIndex = (currentIndex + 1) % roiArray.length;
            const nextRoi = roiArray[nextIndex];
            setSelectedROI(nextRoi.roiNumber);
        }

        if (status) {
            onStatusChange(status);
            const areAllRoisApproved =
                currentStructureSet?.getRoiByApprovalStatus(
                    DICOMApprovalStatus.APPROVED
                ).length === currentStructureSet?.numberOfStructures;
            const areAllRoisRejected =
                currentStructureSet?.getRoiByApprovalStatus(
                    DICOMApprovalStatus.REJECTED
                ).length === currentStructureSet?.numberOfStructures;

            if (areAllRoisApproved) {
                setStructureSetApprovalStatus(DICOMApprovalStatus.APPROVED);
            } else if (areAllRoisRejected) {
                setStructureSetApprovalStatus(DICOMApprovalStatus.REJECTED);
            } else {
                setStructureSetApprovalStatus(DICOMApprovalStatus.UNAPPROVED);
            }
        }
    };

    const reviewedStructuresAmount = currentStructureSet?.roiArray.filter(
        (struct) => struct.approvalStatus !== DICOMApprovalStatus.UNAPPROVED
    ).length;

    return (
        <Fragment>
            <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" align="center" fontSize={14}>
                    Structures reviewed
                </Typography>
                <Typography variant="body2" align="center" fontSize={14}>
                    {reviewedStructuresAmount} /{' '}
                    {currentStructureSet?.roiArray.length}{' '}
                </Typography>
            </Box>
            <Stack
                spacing={1}
                direction="row"
                justifyContent="space-evenly"
                mt={2}
            >
                <Tooltip title="Approve">
                    <Button
                        variant="outlined"
                        color="success"
                        fullWidth
                        size="small"
                        onClick={() =>
                            handleReview(DICOMApprovalStatus.APPROVED)
                        }
                        sx={{
                            ...(roi.approvalStatus ===
                            DICOMApprovalStatus.APPROVED
                                ? {
                                      backgroundColor: 'success.main',
                                      color: 'success.contrastText',
                                  }
                                : {}),
                        }}
                        data-testid="approve-button"
                    >
                        <Check />
                    </Button>
                </Tooltip>
                <Tooltip title="Reject">
                    <Button
                        variant="outlined"
                        color="error"
                        fullWidth
                        size="small"
                        onClick={() =>
                            handleReview(DICOMApprovalStatus.REJECTED)
                        }
                        sx={{
                            ...(roi.approvalStatus ===
                            DICOMApprovalStatus.REJECTED
                                ? {
                                      backgroundColor: 'error.main',
                                      color: 'error.contrastText',
                                  }
                                : {}),
                        }}
                    >
                        <X />
                    </Button>
                </Tooltip>
                <Tooltip title="Next">
                    <Button
                        variant="outlined"
                        size="small"
                        fullWidth
                        onClick={() => handleReview()}
                    >
                        <SkipNext />
                    </Button>
                </Tooltip>
            </Stack>
        </Fragment>
    );
}
